import { useState, useEffect, useContext } from 'react'
import cn from 'classnames'
import { Typography, IconCtaArrow } from '@grammarly/badger-ui'
import { Icon, InterfaceTransformIcon } from '@grammarly/design-system'
import { SignUpToolsBanner } from '@grammarly/website-components'

import { SignUpProductRecButton } from '../../../item/cta-button/signup-product-recommendation'
import { EventProps } from '../../../../lib/tracking/gnar'
import { getConfig } from '../../../../config'
import { navHelper } from '../../../../lib/services/navigation'
import { composeUrl } from '../../../../lib/utils/url'
import { PageContext } from '../../../../contexts/page-context'
import { gnarSpec } from '../../../../lib/tracking/gnar'
import { useSignUpAction } from '../../../item/cta-button/signup-product-recommendation/hooks/useSignupAction'

import styles from './ai-detector-desktop.module.scss'

const { TS } = Typography

interface AIDetectorDesktopFooterProps {
  aiDetectorState: string
  isDynamicBannerTreatment: boolean
  gnarTrackFn: (trackEventProps: EventProps) => Promise<void>
}

const config = getConfig()

const AIDetectorBanner = () => {
  return (
    <div className={styles.bannerWrapper}>
      <TS as="span">Acknowledge AI usage with instant citations formatted in MLA, APA, or Chicago style.</TS>
      <SignUpProductRecButton
        compact
        className={styles.bannerPrompt}
        text={
          <>
            <TS as="span">
              <b>Get Grammarly</b> It&apos;s free
            </TS>
            <div className={styles.bannerPromptArrow}>
              <Icon icon={InterfaceTransformIcon} accessibilityLabel="" variant="inherit" />
            </div>
          </>
        }
      />
    </div>
  )
}

const AIDetectorSignUpBanner = ({ gnarTrackFn }: { gnarTrackFn: (trackEventProps: EventProps) => Promise<void> }) => {
  const { pageId, slug } = useContext(PageContext)

  const onSignUpClick = useSignUpAction({ placement: 'Section' })

  const signUpUrl = composeUrl(config.paths.funnel.signup, navHelper.currentQueryParams)

  const handleSignUpClick = () => {
    gnarSpec.bannerClick('AI detector SignUp banner', pageId, slug, signUpUrl)
    onSignUpClick()
  }

  const signUpCTAs = [
    {
      btnText: (
        <Typography.TS as="span">
          <b>Get Grammarly </b> It&apos;s free
        </Typography.TS>
      ),
      targetUrl: signUpUrl,
      onClick: handleSignUpClick,
      ariaLabel: "Get Grammarly It's free",
      ctaIcon: {
        iconDirection: 'right' as 'right' | 'left',
        iconComponent: <IconCtaArrow />,
      },
    },
  ]

  return (
    <SignUpToolsBanner
      clientName="cms"
      text="Acknowledge AI usage with instant citations formatted in MLA, APA, or Chicago style."
      gnarTrackFn={gnarTrackFn}
      ctas={signUpCTAs}
      bannerType="AI Detector Tools"
      delay={1500}
      animationType="slideIn"
    />
  )
}

export const AIDetectorDesktopFooter = ({
  aiDetectorState,
  isDynamicBannerTreatment,
  gnarTrackFn,
}: AIDetectorDesktopFooterProps) => {
  const [showDBExperimentBanner, setShowDBExperimentBanner] = useState(false)

  useEffect(() => {
    // If user is in an experiment treatment, show banner only when aiDetectorState is 'done'
    if (aiDetectorState === 'done' && isDynamicBannerTreatment) {
      setShowDBExperimentBanner(true)
    }
  }, [aiDetectorState, isDynamicBannerTreatment])

  const renderFooterBanner = () => {
    if (showDBExperimentBanner && isDynamicBannerTreatment) {
      return <AIDetectorSignUpBanner gnarTrackFn={gnarTrackFn} />
    }

    if (!isDynamicBannerTreatment) {
      return <AIDetectorBanner />
    }
    return null
  }

  return (
    <div aria-live="polite" className={cn({ [styles.dynamicBanner]: isDynamicBannerTreatment })}>
      {renderFooterBanner()}
    </div>
  )
}
